import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, Percent, DollarSign, Tag, Copy } from 'lucide-react';

export default function Coupons() {
  const [coupons, setCoupons] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ code: '', type: 'percentage', value: 10, max_uses: '', min_order: '' });

  useEffect(() => {
    api.get('/coupons').then(r => setCoupons(r.data)).catch(() => {});
  }, []);

  const create = async () => {
    if (!form.code.trim()) { toast.error('Code is required'); return; }
    if (!form.value || form.value <= 0) { toast.error('Value must be greater than 0'); return; }
    try {
      const payload = {
        ...form,
        code: form.code.trim().toUpperCase(),
        value: parseFloat(form.value),
        max_uses: form.max_uses ? parseInt(form.max_uses) : null,
        min_order: form.min_order ? parseFloat(form.min_order) : 0,
      };
      const { data } = await api.post('/coupons', payload);
      setCoupons(prev => [data, ...prev]);
      setShowCreate(false);
      setForm({ code: '', type: 'percentage', value: 10, max_uses: '', min_order: '' });
      toast.success(`Coupon ${data.code} created!`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const deleteCoupon = async (id) => {
    if (!window.confirm('Delete this coupon?')) return;
    await api.delete(`/coupons/${id}`);
    setCoupons(prev => prev.filter(c => c.id !== id));
    toast.success('Deleted');
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`Copied: ${code}`);
  };

  return (
    <div className="space-y-6" data-testid="coupons-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Outfit']">Coupons</h1>
          <p className="text-muted-foreground mt-1">{coupons.length} coupon{coupons.length !== 1 ? 's' : ''}</p>
        </div>
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild>
            <Button size="sm" className="bg-[#1A9DA5] hover:bg-[#158D92] text-white" data-testid="new-coupon-btn">
              <Plus className="w-4 h-4 mr-1" />New Coupon
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>Create Coupon</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-1">
                <Label>Coupon Code</Label>
                <Input value={form.code} onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="e.g., LAUNCH20" className="uppercase font-mono" data-testid="coupon-code-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Discount Type</Label>
                  <Select value={form.type} onValueChange={v => setForm({ ...form, type: v })}>
                    <SelectTrigger data-testid="coupon-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage">Percentage (%)</SelectItem>
                      <SelectItem value="fixed">Fixed Amount ($)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Value</Label>
                  <Input type="number" min="0" step={form.type === 'percentage' ? '1' : '0.01'} value={form.value} onChange={e => setForm({ ...form, value: e.target.value })} data-testid="coupon-value-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Max Uses <span className="text-muted-foreground text-xs">(optional)</span></Label>
                  <Input type="number" min="1" value={form.max_uses} onChange={e => setForm({ ...form, max_uses: e.target.value })} placeholder="Unlimited" data-testid="coupon-max-uses" />
                </div>
                <div className="space-y-1">
                  <Label>Min Order ($) <span className="text-muted-foreground text-xs">(optional)</span></Label>
                  <Input type="number" min="0" step="0.01" value={form.min_order} onChange={e => setForm({ ...form, min_order: e.target.value })} placeholder="0" data-testid="coupon-min-order" />
                </div>
              </div>
              <Button onClick={create} className="w-full bg-[#1A9DA5] hover:bg-[#158D92] text-white" data-testid="create-coupon-btn">Create Coupon</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {coupons.map(c => (
          <Card key={c.id} data-testid={`coupon-card-${c.code}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Tag className="w-4 h-4 text-[#1A9DA5]" />
                  <span className="font-mono font-bold text-lg">{c.code}</span>
                  <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyCode(c.code)}><Copy className="w-3 h-3" /></Button>
                </div>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => deleteCoupon(c.id)}><Trash2 className="w-3 h-3" /></Button>
              </div>
              <div className="flex items-center gap-2 mb-2">
                <Badge className={c.type === 'percentage' ? 'bg-[#E8735A]/10 text-[#E8735A]' : 'bg-[#F0B429]/10 text-[#F0B429]'}>
                  {c.type === 'percentage' ? <Percent className="w-3 h-3 mr-1" /> : <DollarSign className="w-3 h-3 mr-1" />}
                  {c.type === 'percentage' ? `${c.value}% off` : `$${c.value} off`}
                </Badge>
                <Badge variant="outline" className={c.active ? 'bg-green-500/10 text-green-600' : 'bg-red-500/10 text-red-600'}>
                  {c.active ? 'Active' : 'Inactive'}
                </Badge>
              </div>
              <div className="text-xs text-muted-foreground space-y-0.5">
                <p>Used: {c.uses || 0}{c.max_uses ? ` / ${c.max_uses}` : ' (unlimited)'}</p>
                {c.min_order > 0 && <p>Min order: ${c.min_order}</p>}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {coupons.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="p-10 text-center">
            <Tag className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
            <p className="text-muted-foreground">No coupons yet. Create your first discount code!</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
